import type { ReactNode } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { cn } from '@bliks-io/ui';

type WizardCalloutVariant = 'success' | 'hint' | 'info';

interface WizardCalloutProps {
  variant: WizardCalloutVariant;
  title?: string;
  children: ReactNode;
  className?: string;
}

const VARIANT_CONTAINER: Record<WizardCalloutVariant, string> = {
  success: 'bg-bliks-green-500/8 border-bliks-green-500/25',
  hint: 'bg-bliks-green-500/6 border-bliks-green-500/20',
  info: 'bg-surface-subtle border-line'
};

function CalloutIcon({ variant }: { variant: WizardCalloutVariant }) {
  if (variant === 'success') {
    return (
      <span className="mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-bliks-green-500 text-foreground">
        <Check className="h-[13px] w-[13px]" strokeWidth={3} />
      </span>
    );
  }
  if (variant === 'hint') {
    return (
      <span className="mt-px shrink-0 text-bliks-green-700">
        <Sparkles className="h-[18px] w-[18px]" strokeWidth={1.8} />
      </span>
    );
  }
  return <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-info-solid" />;
}

/**
 * The three flavors of inline notice in the onboarding design: the green
 * done banner, the sparkles hint box, and the neutral info box with a blue
 * dot. Replaces the former StepDoneNotice.
 */
export function WizardCallout({ variant, title, children, className }: WizardCalloutProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border px-4.5 py-4',
        VARIANT_CONTAINER[variant],
        className
      )}
    >
      <CalloutIcon variant={variant} />
      <div className="min-w-0">
        {title && <div className="text-sm font-bold text-foreground">{title}</div>}
        <div
          className={cn(
            'leading-[1.55] text-fg-secondary',
            variant === 'hint' ? 'text-[13.5px]' : 'text-[13px]',
            title && 'mt-0.5'
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
